import { IUser, PublicUserInfo } from '@common/types/user';
import {
    EventProps,
    EventTypes,
    PublicOnSiteEvent,
    PublicWebinarEvent,
    WaitingListProps,
    WaitingListResponse,
} from '@event/types/event';
import { TypedMutationTrigger } from '@reduxjs/toolkit/dist/query/react';
import { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query';

import { createContext, useContext } from 'react';
import { ToastProps } from 'react-toastify/dist/types';

const PublicEventContext = createContext<
    | {
          event: {
              data?: PublicOnSiteEvent | PublicWebinarEvent;
              loading: boolean;
              fetching: boolean;
          };
          oppositeEvent?: {
              data?: PublicOnSiteEvent | PublicWebinarEvent;
              loading: boolean;
              fetching: boolean;
          };
          user: {
              data?: Omit<IUser, 'full_name'>;
              loading: boolean;
          };
          guestUserInfo: {
              data?: PublicUserInfo;
              fetching: boolean;
          };
          isLoggedIn: boolean;
          toastProps: Partial<ToastProps>;
          guestAccessHash: string | null;
          register: {
              fn: TypedMutationTrigger<
                  void,
                  { id: number; type: EventTypes } & {
                      attend_dinner?: boolean;
                      colleagueHash?: string | null;
                      guestAccessHash?: string | null;
                  },
                  BaseQueryFn<string | FetchArgs>
              >;
              loading: boolean;
          };
          unregister: {
              fn: TypedMutationTrigger<
                  void,
                  { id: number; type: EventTypes } & {
                      attend_dinner?: boolean;
                      colleagueHash?: string | null;
                      guestAccessHash?: string | null;
                  },
                  BaseQueryFn<string | FetchArgs>
              >;
              loading: boolean;
          };
          joinWaitingList: {
              fn: TypedMutationTrigger<WaitingListResponse, WaitingListProps, BaseQueryFn<string | FetchArgs>>;
              loading: boolean;
          };
          leaveWaitingList: {
              fn: TypedMutationTrigger<
                  void,
                  EventProps & {
                      colleagueHash?: string | null;
                      guestAccessHash?: string | null;
                  },
                  BaseQueryFn<string | FetchArgs>
              >;
              loading: boolean;
          };
          waitingListSuccess: {
              state: 'none' | 'join' | 'leave';
              setState: (state: 'none' | 'join' | 'leave') => void;
          };
      }
    | undefined
>(undefined);

const usePublicEventContext = () => {
    const context = useContext(PublicEventContext);
    if (!context) throw new Error('usePublicEventContext must be used within a provider');
    return context;
};

export { PublicEventContext, usePublicEventContext };
